import { useState, useEffect } from 'react';
import { useI18n } from '@/contexts/I18nContext';

interface LegalContent {
  content: string;
  loading: boolean;
  error: string | null;
}

/**
 * The published legal documents, each backed by
 * `public/content/legal/<type>.<lang>.md` in both `en` and `pt`.
 *
 * `independent-contractor-agreement` serves the Instructor Service Provision
 * Agreement; the type keeps its original name because the route and the
 * served filenames are both `independent-contractor-agreement` (CKLSM-750).
 *
 * `src/content/legal/` used to hold a second, unserved copy of some of these
 * documents. CKLSM-750 deleted it: `public/` is the only source of truth, and
 * a second copy is somewhere an edit can land without reaching a reader.
 */
export type LegalDocumentType =
  | 'terms-of-use'
  | 'privacy-policy'
  | 'independent-contractor-agreement';

/**
 * Documents already fetched this session, keyed `<type>.<lang>`.
 *
 * These are immutable static assets, so a response stays correct for the life
 * of the page. CKLSM-750 made caching worth doing: the published documents are
 * 48-73KB each, up from 1-24KB, and they now link to one another, so following
 * the Agreement to the Terms and back re-fetched and re-rendered tens of
 * kilobytes and flashed a loading state on a document the reader had just had
 * on screen.
 *
 * A module-level Map rather than TanStack Query — which `CLAUDE.md` names as
 * the convention for *server* state — because this is neither server state nor
 * invalidatable: the content changes only when a new build is deployed, which
 * discards this Map along with the rest of the page.
 */
const documentCache = new Map<string, string>();

/**
 * Fetches already in flight, keyed the same way as `documentCache`.
 *
 * Without this, two mounts of the same document+language racing before the
 * first fetch resolves (e.g. navigating away from a document and back before
 * its ~48-73KB fetch completes) each start their own fetch instead of the
 * second one joining the first. Populated for the duration of the request and
 * cleared once it settles either way, so a later fetch is never served a
 * stale rejected promise.
 */
const inFlightFetches = new Map<string, Promise<string>>();

/**
 * Empties the cache. For tests only.
 *
 * A module-level Map outlives any one component, so without this a test that
 * stubs `fetch` would be served the previous test's document and would assert
 * against content it never stubbed. Call it in `beforeEach`.
 */
export const clearLegalContentCache = (): void => {
  documentCache.clear();
};

export const useLegalContent = (documentType: LegalDocumentType): LegalContent => {
  const { currentLanguage } = useI18n();
  const cacheKey = `${documentType}.${currentLanguage || 'en'}`;
  const cached = documentCache.get(cacheKey);

  const [content, setContent] = useState<string>(cached ?? '');
  // A cache hit has nothing to wait for, so the first render paints the
  // document instead of the loading branch.
  const [loading, setLoading] = useState<boolean>(cached === undefined);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    // Só carregar se tivermos um idioma definido
    if (!currentLanguage) return;

    const hit = documentCache.get(cacheKey);
    if (hit !== undefined) {
      setContent(hit);
      setError(null);
      setLoading(false);
      return;
    }

    // Switching document or language while a fetch is in flight must not let
    // the stale response win and paint the wrong document.
    let cancelled = false;

    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        const language = currentLanguage || 'en';
        const fileName = `${documentType}.${language}.md`;

        let fetchPromise = inFlightFetches.get(cacheKey);
        if (!fetchPromise) {
          fetchPromise = (async () => {
            const response = await fetch(`/content/legal/${fileName}`);
            if (!response.ok) {
              throw new Error(`Failed to load ${documentType} content for language ${language}`);
            }
            return response.text();
          })();
          inFlightFetches.set(cacheKey, fetchPromise);
          // A derived `.finally()` promise rejects too when the source does,
          // and nothing else awaits this particular chain — the real error
          // is already handled below via `await fetchPromise` inside this
          // function's own try/catch, so a rejection here would otherwise be
          // a second, unhandled one.
          fetchPromise.finally(() => inFlightFetches.delete(cacheKey)).catch(() => {});
        }

        const markdownContent = await fetchPromise;
        // Cached even when this render is stale: the bytes are correct for
        // their own key, and the next reader of that key should not re-fetch.
        documentCache.set(cacheKey, markdownContent);
        if (cancelled) return;
        setContent(markdownContent);
      } catch (err) {
        if (cancelled) return;
        console.error('Error loading legal content:', err);
        setError(err instanceof Error ? err.message : 'Unknown error');
        setContent('');
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    loadContent();

    return () => {
      cancelled = true;
    };
  }, [documentType, currentLanguage, cacheKey]);

  return { content, loading, error };
};
